/* eslint-disable react/no-multi-comp */
import React, { useState } from 'react';
import { Modal, DatePicker, Button, Input, Select } from 'antd';
import { CloseOutlined, ClockCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import UploadWithPreview from 'components/common/UploadWithPreview';
import UploadWithPreviewMultiple from 'components/common/UploadWithPreviewMultiple';
import { PodUploadQuestionModal, QuestionModal } from 'components/common/PopupModals';
import { getUIVersion } from 'utils/router-static-data';
import { useEndTripModal } from './useEndTripModal';

const ModalContainer = styled.div`
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 24px 32px 32px;
	gap: 24px;
	width: 600px;
	background: #fff;
	border-radius: 8px;
	position: relative;
`;

const CustomClose = styled.div`
	position: absolute;
	top: 16px;
	right: 16px;
	width: 32px;
	height: 32px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	background: #f5f5f5;
	transition: background-color 0.2s ease;

	&:hover {
		background: #e8e8e8;
	}
`;

const ModalTitle = styled.div`
	font-family: 'Inter', sans-serif;
	font-weight: 600;
	font-size: 20px;
	text-align: center;
	color: #1a1a1a;
	margin-bottom: 8px;
	width: 100%;
`;

const Section = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 20px;
`;

const Row = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
`;

const Label = styled.div`
	width: 120px;
	font-size: 14px;
	font-weight: 500;
	color: #4a4a4a;
`;

const StyledDatePicker = styled(DatePicker)`
	width: 280px;
	height: 40px;
	border: 1px solid #d9d9d9;
	border-radius: 6px;

	&:hover {
		border-color: #40a9ff;
	}

	&.ant-picker-focused {
		border-color: #1890ff;
		box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
	}
`;

const StyledInput = styled(Input)`
	width: 280px;
	height: 40px;
	border: 1px solid #d9d9d9;
	border-radius: 6px;

	&:hover {
		border-color: #40a9ff;
	}

	&:focus {
		border-color: #1890ff;
		box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
	}
`;

const UploadSection = styled.div`
	width: 100%;
	border: 1px solid #d9d9d9;
	border-radius: 6px;
	padding: 16px;
	background: #fafafa;
`;

const UploadLabel = styled.div`
	font-size: 14px;
	font-weight: 500;
	color: #4a4a4a;
	margin-bottom: 12px;
`;

const ChargesTitle = styled.div`
	font-size: 18px;
	font-weight: 600;
	color: #1a1a1a;
	margin-bottom: 16px;
	text-align: center;
`;

const ChargesBox = styled.div`
	width: 100%;
	border: 1px solid #e0e0e0;
	border-radius: 6px;
	padding: 20px;
	background: #f8f9fa;
`;

const ChargesHeader = styled.div`
	display: flex;
	justify-content: space-between;
	margin-bottom: 16px;
	font-weight: 600;
	font-size: 14px;
	color: #1a1a1a;
`;

const ChargesRow = styled.div`
	display: flex;
	justify-content: space-between;
	margin-bottom: 12px;
	align-items: center;
`;

const ChargesLabel = styled.div`
	font-size: 14px;
	color: #4a4a4a;
	font-weight: 500;
	flex: 1;
`;

const ChargesInput = styled(Input)`
	width: 120px;
	height: 36px;
	background: #fff !important;
	border: 1px solid #d0d0d0 !important;
	border-radius: 4px;
	font-size: 14px;
	text-align: right;
	margin-left: 16px;

	&:hover {
		border-color: #40a9ff !important;
	}

	&:focus {
		border-color: #1890ff !important;
		box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
	}

	&.ant-input {
		text-align: right;
	}
`;

// const ChargesTotalRow = styled.div`
// 	display: flex;
// 	justify-content: space-between;
// 	margin-top: 16px;
// 	padding-top: 16px;
// 	border-top: 2px solid #d0d0d0;
// 	font-weight: 700;
// 	font-size: 16px;
// 	color: #1a1a1a;
// 	align-items: center;
// `;

// const TotalLabel = styled.div`
// 	font-weight: 700;
// 	font-size: 16px;
// 	color: #1a1a1a;
// 	flex: 1;
// `;

// const TotalValue = styled.div`
// 	width: 120px;
// 	text-align: center;
// 	font-weight: 700;
// 	font-size: 16px;
// 	color: #1a1a1a;
// 	margin-left: 16px;
// `;

const Actions = styled.div`
	display: flex;
	justify-content: center;
	gap: 16px;
	margin-top: 8px;
`;

const CancelButton = styled(Button)`
	&& {
		width: 120px;
		height: 40px;
		border: 1px solid #ff3b30;
		color: #ff3b30;
		background: #fff;
		font-weight: 500;
		font-size: 14px;
		border-radius: 6px;

		&:hover {
			border-color: #ff1a1a;
			color: #ff1a1a;
			background: #fff;
		}
	}
`;

const EndTripButton = styled(Button)`
	&& {
		width: 120px;
		height: 40px;
		border-radius: 6px;
		background: ${(props) => (props.disabled ? '#b6b6b6' : '#6c757d')};
		color: #fff;
		font-weight: 500;
		font-size: 14px;
		border: none;
		transition: background-color 0.3s ease;

		&:hover {
			background: ${(props) => (props.disabled ? '#b6b6b6' : '#5a6268')};
		}
	}
`;
const chargeArray = [
	['Halt Charges', 'haltVendor', 'haltClient'],
	['Miscellaneous Charges', 'miscVendor', 'miscClient'],
	['Toll Charges', 'tollVendor', 'tollClient'],
	['Unloading Charges', 'unloadVendor', 'unloadClient'],
	['Extra KM', 'extraKmVendor', 'extraKmClient'],
	['Extra HR', 'extraHrVendor', 'extraHrClient'],
	['Labour Charges', 'labourVendor', 'labourClient'],
	['Deduction Amount', 'deductionVendor', 'deductionClient'],
];

// Trip End Reason Modal Component
function TripEndReasonModal({ visible, onClose, onSubmit, tripReason }) {
	const [selectedReason, setSelectedReason] = useState(null);

	const handleSubmit = () => {
		if (selectedReason) {
			onSubmit(selectedReason);
			setSelectedReason(null);
		}
	};

	const handleCancel = () => {
		setSelectedReason(null);
		onClose();
	};

	// Reset selected reason when modal opens/closes
	React.useEffect(() => {
		if (!visible) {
			setSelectedReason(null);
		}
	}, [visible]);

	return (
		<Modal
			open={visible}
			onCancel={handleCancel}
			title="Select Trip End Reason"
			footer={[
				<Button key="cancel" onClick={handleCancel}>
					Cancel
				</Button>,
				<Button
					key="submit"
					type="primary"
					disabled={!selectedReason}
					onClick={handleSubmit}
				>
					Submit
				</Button>,
			]}
			width={500}
		>
			<div style={{ marginBottom: 16 }}>
				<p style={{ marginBottom: 8, color: '#666' }}>
					Start and End KM are the same. Please select a reason for ending the trip:
				</p>
				<Select
					style={{ width: '100%' }}
					placeholder="Select a reason"
					value={selectedReason}
					onChange={setSelectedReason}
					showSearch
					filterOption={(input, option) =>
						option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
					}
					options={tripReason}
				/>
			</div>
		</Modal>
	);
}

TripEndReasonModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	tripReason: PropTypes.array.isRequired,
};

function CustomerProofUpload({
	isV2UI,
	customerProofS3Key,
	customerProofS3Keys,
	setCustomerProofS3Keys,
	handleCustomerProofUpload,
	uploadingProof,
	uploadProofError,
	handleCustomerProofRemove,
	isIntercity,
}) {
	return (
		<div
			style={{
				marginTop: 24,
				padding: '16px 0',
				borderTop: '1px solid #f0f0f0',
			}}
		>
			<UploadLabel>Additional charges customer proof</UploadLabel>
			{isV2UI ? (
				<UploadWithPreviewMultiple
					value={customerProofS3Keys}
					onChange={setCustomerProofS3Keys}
					beforeUpload={handleCustomerProofUpload}
					uploading={uploadingProof}
					disabled={false}
					onFileRemove={handleCustomerProofRemove}
					buttonText="Upload Proof"
					accept={['.pdf', '.jpg', '.jpeg', '.png']}
					entity={isIntercity ? 'proof_of_delivery_upload' : 'trip'}
					multiple
				/>
			) : (
				<UploadWithPreview
					beforeUpload={handleCustomerProofUpload}
					uploading={uploadingProof}
					disabled={false}
					uploadError={uploadProofError}
					uploadedFile={customerProofS3Key}
					onFileRemove={handleCustomerProofRemove}
					buttonText="Upload Proof"
					accept={['.pdf', '.jpg', '.jpeg', '.png']}
					entity={isIntercity ? 'proof_of_delivery_upload' : 'trip'}
				/>
			)}
		</div>
	);
}

CustomerProofUpload.propTypes = {
	isV2UI: PropTypes.bool.isRequired,
	customerProofS3Key: PropTypes.string,
	customerProofS3Keys: PropTypes.arrayOf(PropTypes.string),
	setCustomerProofS3Keys: PropTypes.func.isRequired,
	handleCustomerProofUpload: PropTypes.func.isRequired,
	uploadingProof: PropTypes.bool.isRequired,
	uploadProofError: PropTypes.string,
	handleCustomerProofRemove: PropTypes.func.isRequired,
	isIntercity: PropTypes.bool.isRequired,
};

function TripUploadSection({
	isV2UI,
	isPodRequired,
	isIntercity,
	odometerS3Key,
	setOdometerS3Key,
	podS3Keys,
	setPodS3Keys,
	handleUpload,
	uploading,
	uploadError,
	handleRemove,
	s3Key,
	driverPodPreviewKeys = [],
}) {
	if (isV2UI) {
		return (
			<>
				<UploadSection>
					<UploadLabel>Upload odometer End (Optional)</UploadLabel>
					<UploadWithPreviewMultiple
						value={odometerS3Key ? [odometerS3Key] : []}
						onChange={(files) => setOdometerS3Key(files?.[0] || '')}
						beforeUpload={handleUpload}
						uploading={uploading}
						disabled={false}
						onFileRemove={handleRemove}
						buttonText="Upload"
						accept={['.pdf', '.jpg', '.jpeg', '.png']}
						entity="trip"
						multiple={false}
						maxCount={1}
					/>
				</UploadSection>
				{isPodRequired && (
					<UploadSection>
						<UploadLabel>Upload POD</UploadLabel>
						<UploadWithPreviewMultiple
							value={podS3Keys}
							onChange={setPodS3Keys}
							beforeUpload={handleUpload}
							uploading={uploading}
							disabled={false}
							onFileRemove={handleRemove}
							buttonText="Upload"
							accept={['.pdf', '.jpg', '.jpeg', '.png']}
							entity="proof_of_delivery_upload"
							multiple
						/>

						{driverPodPreviewKeys && driverPodPreviewKeys.length > 0 && (
							<div style={{ marginTop: 10 }}>
								<UploadLabel style={{ marginBottom: 4, color: '#8c8c8c' }}>
									Driver Uploaded (Read Only)
								</UploadLabel>
								<UploadWithPreviewMultiple
									value={driverPodPreviewKeys}
									readonly
									entity="proof_of_delivery_upload"
									multiple
								/>
							</div>
						)}
					</UploadSection>
				)}
			</>
		);
	}

	return (
		<UploadSection>
			<UploadLabel>{isIntercity ? 'Upload POD' : 'Upload odometer End'}</UploadLabel>
			<UploadWithPreview
				beforeUpload={handleUpload}
				uploading={uploading}
				disabled={false}
				uploadError={uploadError}
				uploadedFile={s3Key}
				onFileRemove={handleRemove}
				buttonText="Upload"
				accept={['.pdf', '.jpg', '.jpeg', '.png']}
				entity={isIntercity ? 'proof_of_delivery_upload' : 'trip'}
			/>
		</UploadSection>
	);
}

TripUploadSection.propTypes = {
	isV2UI: PropTypes.bool.isRequired,
	isPodRequired: PropTypes.bool.isRequired,
	isIntercity: PropTypes.bool.isRequired,
	odometerS3Key: PropTypes.string,
	setOdometerS3Key: PropTypes.func.isRequired,
	podS3Keys: PropTypes.arrayOf(PropTypes.string),
	setPodS3Keys: PropTypes.func.isRequired,
	handleUpload: PropTypes.func.isRequired,
	uploading: PropTypes.bool.isRequired,
	uploadError: PropTypes.string,
	handleRemove: PropTypes.func.isRequired,
	s3Key: PropTypes.string,
	driverPodPreviewKeys: PropTypes.arrayOf(PropTypes.string),
};

function EndTripModal({ visible, onClose, trip, tableRefreshRef, setpaginateConfig }) {
	const isV2UI = getUIVersion() === 'new';
	const {
		// State
		date,
		setDate,
		endKm,
		setEndKm,
		s3Key,
		podS3Keys,
		setPodS3Keys,
		odometerS3Key,
		setOdometerS3Key,
		uploading,
		uploadError,
		charges,
		loading,
		showPodQuestionModal,
		showForceUploadModal,
		showTripEndReasonModal,
		trip_reason,
		// vendorTotal,
		// clientTotal,
		canSubmit,
		isIntercity,
		isPodRequired,

		// Actions
		handleUpload,
		handleRemove,
		handleChange,
		handleSubmit,
		resetAll,
		handlePodUploadYes,
		handlePodUploadNo,
		handleForceUpload,
		handleTripEndReasonSubmit,
		handleTripEndReasonCancel,
		setShowPodQuestionModal,
		setShowForceUploadModal,
		customerProofS3Key,
		customerProofS3Keys,
		setCustomerProofS3Keys,
		uploadingProof,
		uploadProofError,
		handleCustomerProofUpload,
		handleCustomerProofRemove,
		driverPodPreviewKeys,
	} = useEndTripModal(trip, visible, onClose, tableRefreshRef, setpaginateConfig);

	const shouldAskPodQuestion = isV2UI ? isPodRequired : isIntercity;
	let podUploadQuestion = 'Do you want to Upload odometer reading without LR ?';
	if (shouldAskPodQuestion) {
		podUploadQuestion = 'Do you want to Upload POD without LR ?';
	}

	const shouldAskForceUploadQuestion = isV2UI ? isPodRequired : isIntercity;
	let forceUploadQuestion = 'Upload failed. Do you want to try again?';
	if (shouldAskForceUploadQuestion) {
		forceUploadQuestion =
			'Client reference numbers not found in the file. Do you want to force upload?';
	}

	return (
		<>
			<Modal
				open={visible}
				onCancel={resetAll}
				footer={null}
				width={600}
				closable={false}
				maskClosable={false}
				bodyStyle={{ padding: 0, background: 'transparent' }}
				style={{ borderRadius: 8 }}
				destroyOnClose
			>
				<ModalContainer>
					<CustomClose onClick={resetAll}>
						<CloseOutlined style={{ fontSize: 16, color: '#000' }} />
					</CustomClose>
					<ModalTitle>{isV2UI ? 'Close Trip*' : 'End Trip*'}</ModalTitle>
					<Section>
						<Row>
							<Label>End Date</Label>
							<StyledDatePicker
								showTime
								value={date}
								onChange={setDate}
								format="M/D/YYYY - HH:mm:ss"
								allowClear={false}
								suffixIcon={<ClockCircleOutlined style={{ color: '#999' }} />}
							/>
						</Row>
						{(isV2UI || !isIntercity) && (
							<>
								<Row>
									<Label>End KM</Label>
									<StyledInput
										value={endKm}
										onChange={(e) => {
											const { value } = e.target;
											if (/^\d*$/.test(value)) {
												setEndKm(value);
											}
										}}
										placeholder="0"
									/>
								</Row>
								<Row style={{ marginTop: -8 }}>
									<Label></Label>
								</Row>
							</>
						)}
					</Section>
					<ChargesTitle>Additional Charges</ChargesTitle>
					<ChargesBox>
						<ChargesHeader>
							<div style={{ flex: 1 }}></div>
							<div style={{ width: 120, textAlign: 'center' }}>Vendor</div>
							<div style={{ width: 120, textAlign: 'center' }}>Client</div>
						</ChargesHeader>

						{chargeArray.map(([label, vKey, cKey]) => (
							<ChargesRow key={vKey}>
								<ChargesLabel>{label}</ChargesLabel>
								<ChargesInput
									prefix="₹ "
									value={charges[vKey]}
									onChange={(e) => handleChange(vKey, e.target.value)}
									placeholder="0"
								/>
								<ChargesInput
									prefix="₹ "
									value={charges[cKey]}
									onChange={(e) => handleChange(cKey, e.target.value)}
									placeholder="0"
								/>
							</ChargesRow>
						))}

						<CustomerProofUpload
							isV2UI={isV2UI}
							customerProofS3Key={customerProofS3Key}
							customerProofS3Keys={customerProofS3Keys}
							setCustomerProofS3Keys={setCustomerProofS3Keys}
							handleCustomerProofUpload={handleCustomerProofUpload}
							uploadingProof={uploadingProof}
							uploadProofError={uploadProofError}
							handleCustomerProofRemove={handleCustomerProofRemove}
							isIntercity={isIntercity}
						/>

						{/* <ChargesTotalRow>
							<TotalLabel>GRAND TOTAL</TotalLabel>
							<TotalValue>₹ {vendorTotal.toLocaleString()}</TotalValue>
							<TotalValue>₹ {clientTotal.toLocaleString()}</TotalValue>
						</ChargesTotalRow> */}
					</ChargesBox>
					<TripUploadSection
						isV2UI={isV2UI}
						isPodRequired={isPodRequired}
						isIntercity={isIntercity}
						odometerS3Key={odometerS3Key}
						setOdometerS3Key={setOdometerS3Key}
						podS3Keys={podS3Keys}
						setPodS3Keys={setPodS3Keys}
						handleUpload={handleUpload}
						uploading={uploading}
						uploadError={uploadError}
						handleRemove={handleRemove}
						s3Key={s3Key}
						driverPodPreviewKeys={driverPodPreviewKeys}
					/>
					<Actions>
						<CancelButton onClick={resetAll}>Cancel</CancelButton>
						<EndTripButton
							type="primary"
							htmlType="button"
							disabled={!canSubmit || loading}
							loading={loading}
							onClick={handleSubmit}
						>
							{isV2UI ? 'Close Trip' : 'End Trip'}
						</EndTripButton>
					</Actions>
				</ModalContainer>
			</Modal>
			<PodUploadQuestionModal
				visible={showPodQuestionModal}
				onClose={() => setShowPodQuestionModal(false)}
				question={podUploadQuestion}
				onYes={handlePodUploadYes}
				onNo={handlePodUploadNo}
			/>
			<QuestionModal
				visible={showForceUploadModal}
				onClose={() => setShowForceUploadModal(false)}
				question={forceUploadQuestion}
				onYes={handleForceUpload}
				onNo={() => setShowForceUploadModal(false)}
			/>
			<TripEndReasonModal
				visible={showTripEndReasonModal}
				onClose={handleTripEndReasonCancel}
				onSubmit={handleTripEndReasonSubmit}
				tripReason={trip_reason}
			/>
		</>
	);
}

EndTripModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	tableRefreshRef: PropTypes.object,
	setpaginateConfig: PropTypes.func,
	trip: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		trip_type: PropTypes.string,
		city: PropTypes.string,
		project_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		vendor_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		vehicle_type: PropTypes.string,
		vehicle_number: PropTypes.string,
		reporting_date: PropTypes.string,
		reporting_time: PropTypes.string,
		start_date: PropTypes.string,
		start_time: PropTypes.string,
		start_km: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		actual_end_km: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		end_date: PropTypes.string,
		end_time: PropTypes.string,
		actual_end_time: PropTypes.string,
		is_valid: PropTypes.bool,
		source_pincode: PropTypes.string,
		destination_pincode: PropTypes.string,
		info: PropTypes.shape({
			is_inter_city: PropTypes.bool,
			pod_required: PropTypes.bool,
			driver_name: PropTypes.string,
			driver_number: PropTypes.string,
			client_rate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			vendor_rate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			requirement_proof: PropTypes.arrayOf(PropTypes.string),
		}),
		pod_required: PropTypes.bool,
		trip_timings: PropTypes.shape({
			actual_end_time: PropTypes.string,
			actual_end_km: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			end_km: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		}),
	}).isRequired,
};

export default EndTripModal;
